import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CFSectionLocal, CreatePromotionModel } from 'domains/promotion/Promotion.types';
import { MarginBox, PlusCircleButton, Text } from 'UI';
import AddPromotedCommercialFamily from './AddPromotedCommercialFamily';
import { AlreadyExistingCommercialFamilyCodeNotification } from './PromotionErrorNotifications';

export function CommercialFamilyDiscountRow({
  promotion,
  setPromotion,
  setCurrentStep,
  maxCommercialFamiliesGroups,
}: Readonly<{
  promotion: CreatePromotionModel;
  setPromotion: (x: CreatePromotionModel) => void;
  setCurrentStep: (x: number) => void;
  maxCommercialFamiliesGroups: number;
}>) {
  const { t } = useTranslation();
  const [displayAddButton, setDisplayAddButton] = useState<boolean>(false);

  const commercialFamilyCodesSections = useMemo(() => promotion.commercialFamilyCodes ?? [{ items: [], discount: 0 }], [
    promotion,
  ]);

  const addLine = () => {
    if (promotion.commercialFamilyCodes && promotion.commercialFamilyCodes.length >= maxCommercialFamiliesGroups) {
      return;
    }
    setDisplayAddButton(false);
    setPromotion({
      ...promotion,
      commercialFamilyCodes: [...(promotion.commercialFamilyCodes ?? []), { items: [], discount: 0 }],
    });
  };

  const handleDisplayAddButton = useCallback(() => {
    setDisplayAddButton(
      commercialFamilyCodesSections.filter(
        (section: CFSectionLocal) => section.items.length === 0 && section.discount === 0,
      ).length === 0 && commercialFamilyCodesSections.length < maxCommercialFamiliesGroups,
    );
  }, [commercialFamilyCodesSections, maxCommercialFamiliesGroups]);

  useEffect(() => {
    handleDisplayAddButton();
  }, [handleDisplayAddButton]);

  return (
    <>
      <AlreadyExistingCommercialFamilyCodeNotification
        promotion={promotion}
        setPromotion={setPromotion}
        setCurrentStep={setCurrentStep}
      />
      <AddPromotedCommercialFamily
        promotion={promotion}
        setPromotion={setPromotion}
        setCurrentStep={setCurrentStep}
        data-cy={'commercial-families-section'}
      />
      {displayAddButton && (
        <>
          <MarginBox mt={30} />
          <PlusCircleButton onClick={addLine} data-cy={'add-commercial-family-button'}>
            <Text type="base" bold color="clear_blue" decoration={'underline'}>
              {t('backoffice.promotion.add_commercial_family', 'Add commercial family')}
            </Text>
          </PlusCircleButton>
        </>
      )}
    </>
  );
}
